/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, signal } from '@angular/core';

import { AnalyticsService } from '@shure/cloud/shared/utils/analytics';

import { Cookiebot } from '../models/cookiebot.models';
import { addCookiebotListener, removeCookiebotListener } from '../utils/cookiebot.utils';

declare global {
	interface Window {
		Cookiebot?: Cookiebot;
	}
}

@Injectable()
export class CookiebotService {
	public readonly isCookiebotReady = signal(false);

	constructor(private analyticsService: AnalyticsService) {}

	/**
	 * Adds necessary Cookiebot listeners for analytics
	 */
	public addCookiebotListeners(): void {
		addCookiebotListener('CookiebotOnConsentReady', this.cookieBotReadyListener);
		addCookiebotListener('CookiebotOnAccept', this.cookieBotAcceptListener);
		addCookiebotListener('CookiebotOnDecline', this.cookieBotDeclineListener);
	}

	/**
	 * Removes Cookiebot listeners, to be called on component destroy
	 */
	public removeCookiebotListeners(): void {
		removeCookiebotListener('CookiebotOnConsentReady', this.cookieBotReadyListener);
		removeCookiebotListener('CookiebotOnAccept', this.cookieBotAcceptListener);
		removeCookiebotListener('CookiebotOnDecline', this.cookieBotDeclineListener);
	}

	/**
	 * Note: this is not checking whether a user has consented, only that Cookiebot is ready (Google Tag Manager does this)
	 * Occurs at page load and every time the value consent is affected (order is Ready -> Loaded -> Accept/Decline)
	 */
	public cookieBotReadyListener = (): void => {
		const marketingCookieConsent = window.Cookiebot?.consent.marketing ?? false;
		this.analyticsService.isAnalyticsOptedIn.set(marketingCookieConsent);
		this.isCookiebotReady.set(true);
	};

	/**
	 * Occurs at page load and thereafter whenever any category of cookies is accepted by user (for example, if user
	 * 1) accepts all three categories, then 2) removes one category--this event still fires twice)
	 */
	public cookieBotAcceptListener = (): void => {
		if (this.analyticsService.isMixpanelInitialized()) {
			if (!this.analyticsService.isAnalyticsOptedIn()) {
				this.analyticsService.sendOptOutEvent();
			} else {
				this.analyticsService.sendOptInEvent();
			}
		}
	};

	/**
	 * Occurs at page load and thereafter whenever all cookies have been declined by user
	 */
	public cookieBotDeclineListener = (): void => {
		if (this.analyticsService.isMixpanelInitialized()) {
			this.analyticsService.sendOptOutEvent();
		}
	};
}
